import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { BackToTop } from "@/components/feature/BackToTop";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { faqs, siteInfo } from "@/mocks/articles";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import Hero from "./components/Hero";
import HotTicker from "./components/HotTicker";
import FeatureSection from "./components/FeatureSection";
import CategorySection from "./components/CategorySection";
import LatestSection from "./components/LatestSection";
import VoicesSection from "./components/VoicesSection";

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      name: siteInfo.name,
      url: `${SITE_ORIGIN}/`,
      inLanguage: "zh-CN",
      description: "看料吃瓜专注全网热点事件的深度复盘，从细节到内幕层层拆解，提供独家梳理与背景分析。",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_ORIGIN}/insights?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Organization",
      name: siteInfo.name,
      url: `${SITE_ORIGIN}/`,
      email: siteInfo.email,
      description: siteInfo.intro,
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ],
};

export default function Home() {
  usePageMeta({
    title: "看料吃瓜｜文娱热点深度复盘与全网事件内幕解读",
    description:
      "看料吃瓜专注全网热点事件的深度复盘，从细节到内幕层层拆解，提供独家梳理与背景分析。看料吃瓜深挖文娱圈与网络热议事件，做有逻辑、有依据的吃瓜复盘。",
    path: "/",
    keywords: "看料吃瓜,看料吃瓜深挖,看料吃瓜复盘,吃瓜内幕解读,热点事件复盘",
    type: "website",
    jsonLd: HOME_JSON_LD,
  });

  const benefits = [
    "每个工作日一封，只发值得读的内容",
    "新复盘上线第一时间提醒",
    "重大事件的补充线索与更正说明",
    "不含广告，不做营销推送",
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <Hero />
        <HotTicker />
        <FeatureSection />
        <CategorySection />
        <LatestSection />
        <VoicesSection />

        <section id="faq" className="mx-auto max-w-editorial scroll-mt-24 px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-4">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
                常见问题
              </p>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[28px]">
                关于看料吃瓜，你可能想知道的
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-600">
                如果你的问题没有在这里得到回答，欢迎直接给我们发邮件，我们会在两个工作日内回复。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                {siteInfo.email}
              </a>
            </div>
            <div className="lg:col-span-8">
              <FaqAccordion items={faqs} />
            </div>
          </div>
        </section>

        <section id="subscribe" className="scroll-mt-24 border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                  <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
                  每日瓜料简报
                </p>
                <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[32px]">
                  不用刷遍全网，
                  <br />
                  每天花五分钟看清重点
                </h2>
                <ul className="mt-6 space-y-3.5">
                  {benefits.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-fill mt-0.5 text-secondary-500" aria-hidden="true"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center">
                <div className="w-full">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}